import React, { useState, useEffect, useCallback } from "react";
import { db } from "../utils/firebase";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Users } from "lucide-react";
import moment from "moment";
import { Helmet } from "react-helmet";
import { useAuth } from "../hooks/AuthenticationProvider";

const questionnairePeriods = [
  { name: "Intake", days: 0 },
  { name: "1 Month", days: 30 }, // NEW
  { name: "2 Months", days: 60 }, // NEW
  { name: "3 Months", days: 90 },
  { name: "6 Months", days: 180 },
  { name: "9 Months", days: 270 },
  { name: "1 Year", days: 365 },
  { name: "18 Months", days: 548 },
  { name: "2 Years", days: 730 },
];

const RecoveryCapitalData = () => {
  const navigate = useNavigate();
  const { organizationId, organizationData, permissions, checkPermission } =
    useAuth();
  const residents = organizationData?.residents || [];

  const [isComparisonModalOpen, setComparisonModalOpen] = useState(false);
  const [todoList, setTodoList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletedTodos, setDeletedTodos] = useState(new Set());
  const [selectedResidentQuestionnaires, setSelectedResidentQuestionnaires] =
    useState([]);
  const [topScores, setTopScores] = useState({
    generalCapitalScore: [],
    individualCapitalScore: [],
    physicalCapitalScore: [],
    spiritualCapitalScore: [],
    socialCapitalScore: [],
    recoveryCapitalTotal: [],
  });
  const [selectedPeriod, setSelectedPeriod] = useState("Intake");
  const [scoreRange, setScoreRange] = useState("Top");
  const [allResidents, setAllResidents] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    Removed: false,
    "Healthy Transition": false,
    Relapsed: false,
    MAT: false,
    "No MAT": false,
    Kids: false,
    "No Kids": false,
    Jail: false,
    "No Jail": false,
  });

  const domainOrder = [
    "recoveryCapitalTotal",
    "generalCapitalScore",
    "individualCapitalScore",
    "physicalCapitalScore",
    "socialCapitalScore",
    "spiritualCapitalScore",
  ].map((domain) =>
    domain
      .replace(/([A-Z])/g, " $1")
      .trim()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );

  const [openDomains, setOpenDomains] = useState({
    recoveryCapitalTotal: false,
    individualCapitalScore: false,
    physicalCapitalScore: false,
    spiritualCapitalScore: false,
    socialCapitalScore: false,
    generalCapitalScore: false,
  });

  const toggleDomain = (domain) => {
    setOpenDomains((prevState) => ({
      ...prevState,
      [domain]: !prevState[domain],
    }));
  };

  const selectResident = async (residentName) => {
    if (!organizationId) return;

    const q = query(
      collection(db, `organizations/${organizationId}/recoveryCapital`),
      where("residentName", "==", residentName)
    );
    const querySnapshot = await getDocs(q);

    const questionnaires = querySnapshot.docs
      .map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }))
      .sort((a, b) => a.timestamp.seconds - b.timestamp.seconds);

    setSelectedResidentQuestionnaires(questionnaires);
    setComparisonModalOpen(true);
  };

  useEffect(() => {
    const fetchRecoveryCapitalData = async () => {
      if (!organizationId) return;

      try {
        setIsLoading(true);

        // Step 1: Get all questionnaires
        const questionnairesSnapshot = await getDocs(
          collection(db, `organizations/${organizationId}/recoveryCapital`)
        );

        // Create a map to organize questionnaires by resident name
        const questionnairesByResident = {};

        questionnairesSnapshot.forEach((doc) => {
          const data = doc.data();
          const residentName = data.residentName;

          if (!questionnairesByResident[residentName]) {
            questionnairesByResident[residentName] = {
              fullName: residentName,
              questionnaires: [],
            };
          }

          questionnairesByResident[residentName].questionnaires.push(data);
        });

        // Step 2: Fetch all current residents data and merge
        const currentResidentsMap = {};
        residents.forEach((resident) => {
          currentResidentsMap[resident.fullName] = resident;
        });

        // Step 3: Fetch all alumni data
        console.log("Fetching alumni data...");
        const alumniSnapshot = await getDocs(
          collection(db, `organizations/${organizationId}/alumni`)
        );

        const alumniMap = {};
        alumniSnapshot.forEach((doc) => {
          const alumniData = doc.data();
          if (alumniData.fullName) {
            // Make sure fullName exists
            alumniMap[alumniData.fullName] = alumniData;
          }
        });

        console.log("Alumni data fetched:", alumniMap);

        // Step 4: Merge all data
        const allResidentsWithQuestionnaires = [];

        Object.keys(questionnairesByResident).forEach((residentName) => {
          const baseRecord = questionnairesByResident[residentName];
          const currentResidentData = currentResidentsMap[residentName];
          const alumniData = alumniMap[residentName];

          // Build the combined data object
          const combinedData = {
            ...baseRecord,
            questionnaires: baseRecord.questionnaires || [],
            isAlumni: !currentResidentData && !!alumniData,
            isCurrent: !!currentResidentData,
          };

          // Add alumni specific data
          if (alumniData) {
            // Important fields from alumni record
            combinedData.dob = alumniData.dob;
            combinedData.intakeDate = alumniData.intakeDate;
            combinedData.dischargeDate = alumniData.dischargeDate;
            combinedData.dischargeStatus = alumniData.dischargeStatus;
            combinedData.sobrietyStatus = alumniData.sobrietyStatus;
            combinedData.sobrietyDate = alumniData.sobrietyDate; // Add sobriety date
            combinedData.isMAT = alumniData.isMAT;
          }

          // Current resident data overrides alumni data (except for dischargeDate)
          if (currentResidentData) {
            // Important fields from current resident record
            combinedData.dob = currentResidentData.dob;
            combinedData.intakeDate = currentResidentData.intakeDate;
            combinedData.sobrietyStatus = currentResidentData.sobrietyStatus;
            combinedData.sobrietyDate = currentResidentData.sobrietyDate; // Add sobriety date
            combinedData.isMAT = currentResidentData.isMAT;
            combinedData.dischargeStatus = currentResidentData.dischargeStatus;
            // Preserve dischargeDate from alumni if it exists
            if (!combinedData.dischargeDate && alumniData?.dischargeDate) {
              combinedData.dischargeDate = alumniData.dischargeDate;
            }
          }

          allResidentsWithQuestionnaires.push(combinedData);
        });

        // Sort by name
        allResidentsWithQuestionnaires.sort((a, b) =>
          a.fullName.localeCompare(b.fullName)
        );

        // For each resident, sort their questionnaires by timestamp
        allResidentsWithQuestionnaires.forEach((resident) => {
          if (
            resident.questionnaires &&
            Array.isArray(resident.questionnaires)
          ) {
            resident.questionnaires.sort((a, b) => {
              const timeA = a.timestamp?.seconds || 0;
              const timeB = b.timestamp?.seconds || 0;
              return timeA - timeB;
            });
          }
        });

        setAllResidents(allResidentsWithQuestionnaires);
        setIsLoading(false);
        console.log(
          "Combined residents with questionnaires:",
          allResidentsWithQuestionnaires
        );
      } catch (error) {
        console.error("Error fetching recovery capital data:", error);
        setIsLoading(false);
      }
    };

    fetchRecoveryCapitalData();
  }, [organizationId, residents]);

  const getRecentQuestionnaireData = async (residentName) => {
    if (!organizationId) return null;

    try {
      // First find the resident in your all residents data
      const resident = allResidents.find((r) => r.fullName === residentName);
      if (!resident) return null;

      // Define which filter fields you want to pre-fill
      const filterFields = [
        "overOneYearInPrison",
        "kids",
        "homelessOneYear",
        "childhoodTrauma",
        "twoParentsHome",
        "coOccurringDiagnosis",
      ];

      // If we already have questionnaires in our allResidents state
      if (resident.questionnaires && resident.questionnaires.length > 0) {
        // Sort by timestamp descending to get the most recent first
        const sortedQuestionnaires = [...resident.questionnaires].sort(
          (a, b) => {
            const timeA = a.timestamp?.seconds || 0;
            const timeB = b.timestamp?.seconds || 0;
            return timeB - timeA; // Descending order
          }
        );

        // Extract only the filter fields we want
        const filterData = {};
        for (const field of filterFields) {
          if (sortedQuestionnaires[0][field] !== undefined) {
            filterData[field] = sortedQuestionnaires[0][field];
          }
        }

        return filterData;
      }

      // If no questionnaires in our state, fetch from Firestore
      const q = query(
        collection(db, `organizations/${organizationId}/recoveryCapital`),
        where("residentName", "==", residentName),
        orderBy("timestamp", "desc"),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return null;

      const latestData = querySnapshot.docs[0].data();

      // Extract only filter fields
      const filterData = {};
      for (const field of filterFields) {
        if (latestData[field] !== undefined) {
          filterData[field] = latestData[field];
        }
      }

      return filterData;
    } catch (error) {
      console.error("Error getting recent questionnaire data:", error);
      return null;
    }
  };

  const goToRiskModel = () => {
    navigate("/riskmodel");
  };

  const getFilteredResidents = useCallback(() => {
    return allResidents
      .filter((resident) => {
        for (const [filterKey, isActive] of Object.entries(activeFilters)) {
          if (!isActive) continue;

          switch (filterKey) {
            case "Removed":
            case "Healthy Transition":
              if (resident.dischargeStatus !== filterKey) return false;
              break;
            case "Relapsed":
              if (resident.sobrietyStatus !== "Not Sober") return false;
              break;
            case "MAT":
              if (!resident.isMAT) return false;
              break;
            case "No MAT":
              if (resident.isMAT) return false;
              break;
            case "Kids":
            case "No Kids":
              if (
                !resident.questionnaires?.[0]?.kids ||
                resident.questionnaires[0].kids !==
                  (filterKey === "Kids" ? "Yes" : "No")
              )
                return false;
              break;
            case "Jail":
            case "No Jail":
              if (
                !resident.questionnaires?.[0]?.overOneYearInPrison ||
                resident.questionnaires[0].overOneYearInPrison !==
                  (filterKey === "Jail" ? "Yes" : "No")
              )
                return false;
              break;
          }
        }
        return true;
      })
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [allResidents, activeFilters]);

  const toggleFilter = (filter) => {
    if (!permissions.viewRecoveryCapital) return;
    setActiveFilters((prevFilters) => ({
      ...prevFilters,
      [filter]: !prevFilters[filter],
    }));
  };

  /* -----------------------------------------------------------
   EXPORT ‑ Recovery‑Capital CSV
----------------------------------------------------------- */
  const exportFilteredDataToCSV = useCallback(() => {
    const filteredResidents = getFilteredResidents();
    if (filteredResidents.length === 0) {
      alert("No data to export based on current filters.");
      return;
    }

    /* ───────────────────── HEADERS ───────────────────── */
    const headers = [
      "Full Name",
      "Status", // Current or alumni
      "Age", // Age in years
      "Questionnaire",
      "Date Completed",
      "Intake Date",
      "Discharge Date",
      "Sobriety Date",
      "Days In Recovery",
      "General Capital Score",
      "Social Capital Score",
      "Physical Capital Score",
      "Individual Capital Score",
      "Spiritual Capital Score",
      "Recovery Capital Total",
      "MAT",
      "Has Kids",
      "Strong Kids Relationship", // ← NEW COLUMN
      "Prison Experience",

      /* ↓ NEW DROP‑DOWN COLUMNS ↓ */
      "Homeless > 1 Year",
      "Childhood Trauma",
      "Two Parents Home",
      "Mental Health Issues", // now driven by coOccurringDiagnosis

      // ✨ NEW PHYSICAL HEALTH COLUMNS ✨
      "Sleep Quality", // Raw score (1-4)
      "Healthy Eating", // Raw score (1-4)
      "Regular Exercise", // Raw score (1-4)
      "Good Sleep", // Derived (Yes/No for 3-4)
      "Eats Healthy", // Derived (Yes/No for 3-4)
      "Exercises Regularly", // Derived (Yes/No for 3-4)
      "Physical Wellness Score", // Count of good behaviors (0-3)

      /* ↓ Derived flags (still useful) ↓ */
      "Trauma (Derived)",
      "In Therapy",
      "Purpose",
      "Guilt/Shame",
      "Discharge Status",
      "Sobriety Status",
    ];

    let csvContent = headers.join(",") + "\n";

    /* helper to coerce to number safely */
    const asNum = (v) => (typeof v === "number" ? v : Number(v) || null);

    const isGoodPhysicalHealth = (value) => {
      const n = asNum(value);
      return n === 3 || n === 4;
    };

    filteredResidents.forEach((resident) => {
      /* ── Age ── */
      let age = "N/A";
      if (resident.dob) {
        try {
          const birthDate = new Date(resident.dob);
          const today = new Date();
          age = today.getFullYear() - birthDate.getFullYear();
          const birthdayPassed =
            today.getMonth() > birthDate.getMonth() ||
            (today.getMonth() === birthDate.getMonth() &&
              today.getDate() >= birthDate.getDate());
          if (!birthdayPassed) age--;
        } catch {
          age = "N/A";
        }
      }

      /* ── Loop through questionnaires ── */
      resident.questionnaires.forEach((q) => {
        const status = resident.isCurrent
          ? "Current Resident"
          : resident.isAlumni
          ? "Alumni"
          : "Unknown";

        /* Questionnaire + sobriety dates */
        const toDate = (d) =>
          d?.toDate ? d.toDate() : new Date(d ?? undefined);

        const questionnaireDate = q.timestamp ? toDate(q.timestamp) : null;
        const formattedDate = questionnaireDate
          ? questionnaireDate.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "N/A";

        const sobrietyDate = resident.sobrietyDate
          ? toDate(resident.sobrietyDate)
          : null;
        const formattedSobrietyDate = sobrietyDate
          ? sobrietyDate.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "N/A";

        const daysInRecovery =
          questionnaireDate && sobrietyDate
            ? Math.max(
                0,
                Math.floor(
                  (questionnaireDate.getTime() - sobrietyDate.getTime()) /
                    (1000 * 3600 * 24)
                )
              )
            : "N/A";

        /* Intake / discharge dates */
        const fmt = (d) =>
          d.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });

        const formattedIntakeDate = resident.intakeDate
          ? fmt(toDate(resident.intakeDate))
          : "N/A";
        const formattedDischargeDate = resident.dischargeDate
          ? fmt(toDate(resident.dischargeDate))
          : "N/A";

        /* ✨ NEW PHYSICAL HEALTH PROCESSING ✨ */

        // Raw scores (1-4 scale)
        const sleepRaw = asNum(q.sleep) || "N/A";
        const eatHealthyRaw = asNum(q.eatHealthy) || "N/A";
        const workoutRaw = asNum(q.workoutRegularly) || "N/A";

        // Derived Yes/No (good = 3 or 4)
        const goodSleep =
          sleepRaw !== "N/A"
            ? isGoodPhysicalHealth(sleepRaw)
              ? "Yes"
              : "No"
            : "N/A";
        const eatsHealthy =
          eatHealthyRaw !== "N/A"
            ? isGoodPhysicalHealth(eatHealthyRaw)
              ? "Yes"
              : "No"
            : "N/A";
        const exercisesRegularly =
          workoutRaw !== "N/A"
            ? isGoodPhysicalHealth(workoutRaw)
              ? "Yes"
              : "No"
            : "N/A";

        // Physical wellness score (count of good behaviors)
        const physicalBehaviors = [
          isGoodPhysicalHealth(sleepRaw),
          isGoodPhysicalHealth(eatHealthyRaw),
          isGoodPhysicalHealth(workoutRaw),
        ].filter(Boolean).length;

        const physicalWellnessScore = [
          sleepRaw,
          eatHealthyRaw,
          workoutRaw,
        ].every((v) => v === "N/A")
          ? "N/A"
          : physicalBehaviors;

        /* Direct yes/no from new drop‑downs */
        const homelessOneYear = q.homelessOneYear ?? "N/A";
        const childhoodTrauma = q.childhoodTrauma ?? "N/A";
        const twoParentsHome = q.twoParentsHome ?? "N/A";
        const mentalHealthDx = q.coOccurringDiagnosis ?? "N/A";

        /* Derived trauma flag (updated to remove adverseExperiences) */
        const traumaDerived =
          childhoodTrauma === "Yes" || asNum(q.childhoodImpact) === 1
            ? "Yes"
            : "No";

        /* Therapy flag unchanged */
        const inTherapy =
          asNum(q.therapy) === 3 || asNum(q.therapy) === 4 ? "Yes" : "No";

        /* Purpose flag unchanged */
        const purpose =
          asNum(q.lifeHasPurpose) === 3 || asNum(q.lifeHasPurpose) === 4
            ? "Yes"
            : "No";

        /* Mental‑health issues flag now driven by drop‑down first */
        const mentalHealthIssues =
          mentalHealthDx === "Yes"
            ? "Yes"
            : mentalHealthDx === "No"
            ? "No"
            : asNum(q.mentalHealthProblems) === 1 ||
              asNum(q.mentalHealthProblems) === 2
            ? "Yes"
            : "No";

        const guiltShame =
          asNum(q.guiltOrShame) === 1 || asNum(q.guiltOrShame) === 2
            ? "Yes"
            : "No";

        /* Kids‑related fields */
        const kidsValue = q.kids || "N/A";

        /* NEW: Strong Kids Relationship (only if resident has kids) */
        let strongKidsRelationship = "N/A";
        const relScore = asNum(q.strongRelationKids);
        if (kidsValue === "Yes" && relScore !== null) {
          if (relScore === 3 || relScore === 4) strongKidsRelationship = "Yes";
          else if (relScore === 1 || relScore === 2)
            strongKidsRelationship = "No";
        }

        /* Other existing fields */
        const prisonExperience = q.overOneYearInPrison || "N/A";
        const isMAT = resident.isMAT ? "Yes" : "No";
        const dischargeStatus = resident.dischargeStatus || "N/A";
        const sobrietyStatus = resident.sobrietyStatus || "N/A";

        /* Row assembly */
        const row = [
          resident.fullName,
          status,
          age,
          q.questionnaire,
          formattedDate,
          formattedIntakeDate,
          formattedDischargeDate,
          formattedSobrietyDate,
          daysInRecovery,
          q.generalCapitalScore ?? 0,
          q.socialCapitalScore ?? 0,
          q.physicalCapitalScore ?? 0,
          q.individualCapitalScore ?? 0,
          q.spiritualCapitalScore ?? 0,
          q.recoveryCapitalTotal ?? 0,
          isMAT,
          kidsValue,
          strongKidsRelationship,
          prisonExperience,
          homelessOneYear,
          childhoodTrauma,
          twoParentsHome,
          mentalHealthIssues,

          // ✨ NEW PHYSICAL HEALTH DATA ✨
          sleepRaw, // Raw sleep score (1-4)
          eatHealthyRaw, // Raw eating score (1-4)
          workoutRaw, // Raw exercise score (1-4)
          goodSleep, // Derived sleep (Yes/No)
          eatsHealthy, // Derived eating (Yes/No)
          exercisesRegularly, // Derived exercise (Yes/No)
          physicalWellnessScore, // Count of good behaviors (0-3)

          // Existing derived flags
          traumaDerived,
          inTherapy,
          purpose,
          guiltShame,
          dischargeStatus,
          sobrietyStatus,
        ];

        /* Escape commas */
        const escaped = row.map((v) =>
          typeof v === "string" && v.includes(",") ? `"${v}"` : v
        );
        csvContent += escaped.join(",") + "\n";
      });
    });

    /* ── Download CSV ── */
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "recovery_capital_data.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [getFilteredResidents]);

  // Update handleNotificationClick to include the data passing
  const handleNotificationClick = async (todo) => {
    const recentData = await getRecentQuestionnaireData(todo.residentName);
    navigate("/recoverycapital", {
      state: {
        fromTodo: true,
        residentName: todo.residentName,
        questionnaire: todo.questionnaire,
        overOneYearInPrison: recentData?.overOneYearInPrison || "",
      },
    });
  };

  // The regular button should not pass any state
  const goToRecoveryCapital = () => {
    navigate("/recoverycapital");
  };

  useEffect(() => {
    const fetchDeletedTodos = async () => {
      if (!organizationId) return;

      const deletedTodosRef = collection(
        db,
        `organizations/${organizationId}/deletedTodos`
      );
      const deletedTodosSnapshot = await getDocs(deletedTodosRef);
      const deletedSet = new Set();
      deletedTodosSnapshot.forEach((doc) => {
        deletedSet.add(doc.data().todoId);
      });
      setDeletedTodos(deletedSet);
    };

    fetchDeletedTodos();
  }, [organizationId]);

  const handleManualDelete = async (todoId) => {
    if (!permissions.deleteToDo) return;

    const isConfirmed = window.confirm(
      "Are you sure you want to delete this from the to do list?"
    );
    if (isConfirmed && organizationId) {
      try {
        await addDoc(
          collection(db, `organizations/${organizationId}/deletedTodos`),
          { todoId }
        );
        setDeletedTodos((prev) => new Set(prev).add(todoId));
        setTodoList((prev) => prev.filter((todo) => todo.id !== todoId));
        console.log("Todo deleted successfully");
      } catch (error) {
        console.error("Error deleting todo:", error);
      }
    }
  };

  const periods = questionnairePeriods.map((period) => period.name);

  const getLengthOfStay = (startDate) => {
    return moment().diff(moment(startDate), "days");
  };

  const formatLengthOfStay = (days) => {
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    return `${months} month${months !== 1 ? "s" : ""} and ${remainingDays} day${
      remainingDays !== 1 ? "s" : ""
    }`;
  };

  useEffect(() => {
    let mounted = true;

    const generateTodoList = async () => {
      if (!organizationId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // If there are no residents, set loading to false and return empty list
        if (residents.length === 0) {
          if (mounted) {
            setTodoList([]);
            setIsLoading(false);
          }
          return;
        }

        const questionnairesSnapshot = await getDocs(
          collection(db, `organizations/${organizationId}/recoveryCapital`)
        );

        const completedQuestionnaires = new Map();

        questionnairesSnapshot.forEach((doc) => {
          const data = doc.data();
          if (!completedQuestionnaires.has(data.residentName)) {
            completedQuestionnaires.set(data.residentName, new Set());
          }
          completedQuestionnaires
            .get(data.residentName)
            .add(data.questionnaire);
        });

        const newTodoList = [];

        residents
          .filter((resident) => resident.currentResident === true)
          .forEach((resident) => {
            const lengthOfStay = moment().diff(
              moment(resident.intakeDate),
              "days"
            );

            let lastCompletedIndex = -1;
            questionnairePeriods.forEach((period, index) => {
              if (
                completedQuestionnaires.has(resident.fullName) &&
                completedQuestionnaires.get(resident.fullName).has(period.name)
              ) {
                lastCompletedIndex = index;
              }
            });

            let mostRecentDueIndex = -1;
            questionnairePeriods.forEach((period, index) => {
              if (lengthOfStay >= period.days) {
                mostRecentDueIndex = index;
              }
            });

            if (mostRecentDueIndex > lastCompletedIndex) {
              const period = questionnairePeriods[mostRecentDueIndex];
              const todoId = `${resident.fullName}-${period.name}`;

              if (!deletedTodos.has(todoId)) {
                newTodoList.push({
                  id: todoId,
                  residentName: resident.fullName,
                  questionnaire: period.name,
                  lengthOfStay,
                });
              }
            }
          });

        if (mounted) {
          setTodoList(newTodoList);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error generating todo list:", error);
        if (mounted) {
          setTodoList([]);
          setIsLoading(false);
        }
      }
    };

    generateTodoList();

    return () => {
      mounted = false;
    };
  }, [organizationId, residents, deletedTodos]);

  // Loading indicator component
  const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-40">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
    </div>
  );

  // Top scores fetching
  useEffect(() => {
    const fetchTopScores = async () => {
      if (!organizationId || !permissions.viewRecoveryCapital) return;

      const scoreFields = [
        "recoveryCapitalTotal",
        "individualCapitalScore",
        "physicalCapitalScore",
        "spiritualCapitalScore",
        "socialCapitalScore",
        "generalCapitalScore",
      ];

      const newTopScores = {};

      for (const field of scoreFields) {
        try {
          const scoresRef = collection(
            db,
            `organizations/${organizationId}/recoveryCapital`
          );
          const q = query(
            scoresRef,
            where("questionnaire", "==", selectedPeriod),
            orderBy(field, scoreRange === "Top" ? "desc" : "asc"),
            limit(5)
          );

          const querySnapshot = await getDocs(q);

          newTopScores[field] = querySnapshot.docs.map((doc) => ({
            residentName: doc.data().residentName,
            score: doc.data()[field],
          }));
        } catch (error) {
          console.error(`Error fetching scores for ${field}:`, error);
        }
      }

      setTopScores(newTopScores);
    };

    fetchTopScores();
  }, [
    selectedPeriod,
    scoreRange,
    organizationId,
    permissions.viewRecoveryCapital,
  ]);

  return (
    <>
      <Helmet>
        <title>&bull; Recovery Capital</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        {/* Ambient background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="flex gap-3">
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">
                Recovery Capital Data
              </h1>
            </div>
          </div>

          {/* Recovery Capital Survey Button & Period Selectors */}
          <div className="flex flex-col sm:flex-row gap-4 bg-slate-900/50 backdrop-blur-xl rounded-xl p-4 border border-slate-800/50 relative z-20">
            {checkPermission("addRecoveryCapital") && (
              <>
                <button
                  onClick={goToRecoveryCapital}
                  className="w-full sm:w-auto px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-cyan-500/25"
                >
                  Recovery Capital Survey
                </button>
              </>
            )}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* To Do List Card */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-between p-4 border-b border-slate-800/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-500/10 rounded-lg">
                    <ClipboardList className="text-cyan-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    To Do List
                  </h2>
                </div>
              </div>
              <div className="p-6 max-h-[400px] overflow-y-auto">
                {isLoading ? (
                  <div className="flex justify-center items-center h-40">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-600"></div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {todoList.map((todo) => (
                      <div
                        key={todo.id}
                        className="group relative bg-slate-800/30 rounded-xl p-4 border border-cyan-500/10 hover:border-cyan-500/20 transition-all duration-300"
                      >
                        <div className="flex justify-between items-center">
                          {checkPermission("addRecoveryCapital") ? (
                            <p
                              onClick={() => handleNotificationClick(todo)}
                              className="text-white cursor-pointer hover:text-cyan-300 transition-colors"
                            >
                              {todo.residentName} needs to complete the{" "}
                              {todo.questionnaire} questionnaire
                            </p>
                          ) : (
                            <p className="text-white">
                              {todo.residentName} needs to complete the{" "}
                              {todo.questionnaire} questionnaire
                            </p>
                          )}
                          <button
                            onClick={() => handleManualDelete(todo.id)}
                            className="text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            ×
                          </button>
                        </div>
                        <p className="text-sm text-slate-400 mt-2">
                          Length of stay:{" "}
                          {formatLengthOfStay(todo.lengthOfStay)}
                        </p>
                      </div>
                    ))}
                    {todoList.length === 0 && (
                      <div className="text-center text-cyan-300">
                        No pending questionnaires
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Filters and Compare Card */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative flex items-center justify-between p-4 border-b border-slate-800/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Users className="text-blue-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    Filters & Comparison
                  </h2>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6">
                {/* Filters Column */}
                {/* Filters Column */}
                <div className="space-y-4 relative z-20">
                  <h3 className="text-white font-semibold mb-4">Filters</h3>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-1">
                    {Object.entries(activeFilters).map(([filter, isActive]) => (
                      <label
                        key={filter}
                        className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isActive}
                          onChange={() => toggleFilter(filter)}
                          className="form-checkbox h-4 w-4 text-blue-500 rounded border-slate-600 bg-slate-800 focus:ring-0"
                        />
                        {filter}
                      </label>
                    ))}
                  </div>
                  <div className="pt-4 flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0">
                    <button
                      onClick={() =>
                        setActiveFilters(
                          Object.fromEntries(
                            Object.keys(activeFilters).map((k) => [k, false])
                          )
                        )
                      }
                      className="flex-1 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
                    >
                      Clear Filters
                    </button>
                    {checkPermission("exportRecoveryData") && (
                      <button
                        onClick={exportFilteredDataToCSV}
                        className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        Export CSV
                      </button>
                    )}
                  </div>
                </div>

                {/* Residents Column */}
                <div className="md:col-span-2">
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                    {getFilteredResidents().map((resident, index) => (
                      <div
                        key={index}
                        onClick={() => selectResident(resident.fullName)}
                        className="w-full p-3 text-left bg-slate-800/30 rounded-lg text-white hover:bg-slate-800/50 transition-all duration-300 cursor-pointer relative z-20"
                      >
                        {resident.fullName}
                        {resident.questionnaires && (
                          <span className="text-sm text-slate-400 block">
                            {resident.questionnaires.length} questionnaire
                            {resident.questionnaires.length !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison Modal */}
        {isComparisonModalOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-900 rounded-xl w-full max-w-4xl m-4">
              <div className="p-6 border-b border-slate-800">
                <h2 className="text-2xl font-bold text-white">
                  {selectedResidentQuestionnaires[0]?.residentName}'s Progress
                </h2>
              </div>
              <div className="p-6 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedResidentQuestionnaires.map(
                    (questionnaire, index) => (
                      <div
                        key={index}
                        className="bg-slate-800/50 rounded-xl p-6"
                      >
                        <h3 className="text-xl font-semibold text-white mb-4">
                          {questionnaire.questionnaire}
                        </h3>
                        <div className="space-y-4">
                          {Object.entries(questionnaire)
                            .filter(([key]) => key.includes("Score"))
                            .map(([key, value]) => (
                              <div
                                key={key}
                                className="flex justify-between items-center"
                              >
                                <span className="text-slate-300">
                                  {key
                                    .replace(/([A-Z])/g, " $1")
                                    .trim()
                                    .split(" ")
                                    .map(
                                      (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1)
                                    )
                                    .join(" ")}
                                  :
                                </span>
                                <span className="text-white font-mono">
                                  {value}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
              <div className="p-6 border-t border-slate-800 flex justify-end">
                <button
                  onClick={() => setComparisonModalOpen(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RecoveryCapitalData;
